<template>
    <vx-modal v-model:visible="visible" :padding="false" size="md" ref="modalRef">
        <template v-slot:header>
            <div class="p-6">Section Details</div>
        </template>

        <div class="flex flex-col gap-6 font-sans pt-10 px-12 pb-12">
            <div>
                <vx-label class="text-base!">Section Name</vx-label>
                <vx-input
                    v-model="section.name"
                    size="lg"
                    :error="displayFirstError(validation.name.$errors)"
                ></vx-input>
            </div>

            <toggle-with-content
                title="Schedule Stop"
                subtitle="Mark Campaigns complete and turn off all Automation rules on a future date?"
                v-model="scheduleStopAt"
                size="sm"
            >
                <div class="flex flex-col gap-4">
                    <div v-if="section?.stopAt" class="flex flex-row items-center">
                        <span class="text-sm font-semibold text-slate-700 mr-1">
                            {{ dateFormatted }}
                        </span>
                        <vx-popover hover arrow placement="top">
                            <span
                                class="text-sm font-semibold text-slate-700 underline decoration-dotted decoration-skip-ink-none decoration-thickness-[13.5%] underline-offset-2 underline-position-from-font"
                                data-test="when-date"
                            >
                                {{ tzFormatted }}
                            </span>

                            <template v-slot:content> Uses the team’s time zone. </template>
                        </vx-popover>
                    </div>

                    <vx-date-time-picker
                        ref="datePicker"
                        v-model="section.stopAt"
                        :timezone="getClientTz()"
                        :minDate="dayjs().tz(getClientTz()).toDate()"
                        :isRequired="scheduleStopAt"
                        :rules="{ minutes: { interval: 15 } }"
                    >
                    </vx-date-time-picker>
                    <div v-if="validation.stopAt.$errors" class="text-xs text-rose-800" ref="stopAtErrorRef">
                        {{ displayFirstError(validation.stopAt.$errors) }}
                    </div>
                </div>
            </toggle-with-content>
        </div>
        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button color="muted" size="lg" @click="visible = false"> Cancel </vx-button>

                <vx-button
                    class="grow"
                    :loading="submitting"
                    @click.prevent="submit"
                    color="primary"
                    data-test="submit-button"
                    size="lg"
                    type="button"
                >
                    {{ props.modelValue?.name ? 'Update' : 'Add Section' }}
                    <font-awesome-icon v-if="!props.modelValue?.name" :icon="faCirclePlus" widthAuto />
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxDateTimePicker, VxInput, VxLabel, VxModal, VxPopover } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import { computed, ref, watch } from 'vue';
import { alphanumericDashUnderscoreSpace, displayFirstError, validateRules } from '~/utils/validation';
import { useFlow } from './useFlow';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import ToggleWithContent from '~/components/general/ToggleWithContent.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { formatTz, getClientTz } from '~/utils/date';
import dayjsLib, { toUTCDateTimeFormat } from '~/utils/dayjs';
import type { FlowSection } from '~/types/flow.types';

const dayjs = dayjsLib;
const { saveSections, sections, addSection } = useFlow();

const props = defineProps<{ modelValue?: { name: string; stopAt?: string } }>();
const visible = defineModel<boolean>('visible', { required: true });

const modalRef = ref();
const stopAtErrorRef = ref();

const scheduleStopAt = ref<boolean>(false);
const section = ref<{ name: string; stopAt: Date | undefined }>({ name: '', stopAt: undefined });

const defaultName = computed(() => `Step ${sections.value.length + 1}`);
const dateFormatted = computed(() =>
    section.value?.stopAt ? dayjs(section.value.stopAt).tz(getClientTz()).format('MMM DD, YYYY [at] h:mm a') : null
);
const tzFormatted = computed(() => formatTz(dayjs().tz(getClientTz()).format('z')));

watch(scheduleStopAt, (newValue) => {
    if (!newValue && section.value) {
        section.value.stopAt = undefined;
    }
});

const { $externalResults, catchError } = useFormRequestHandler();

const submitting = ref(false);

const validation = useVuelidate(
    {
        name: {
            required,
            maxLength: maxLength(80),
            regex: alphanumericDashUnderscoreSpace,
        },
        stopAt: {
            requiredIf: helpers.withMessage(
                'The value is required if the Schedule Stop field is enabled',
                requiredIf(() => scheduleStopAt.value)
            ),
        },
    },
    section,
    { $externalResults }
);

const submit = async () => {
    const isValid = await validateRules(validation);
    if (submitting.value || !isValid) {
        if (validation.value.stopAt.$errors.length > 0 && !validation.value.name.$errors.length) {
            modalRef.value.scrollToElement(stopAtErrorRef.value);
        }

        return;
    }

    try {
        submitting.value = true;

        const name = section.value.name;
        const stopAt = section.value.stopAt
            ? toUTCDateTimeFormat(dayjs(section.value.stopAt).tz(getClientTz()))
            : undefined;

        if (props.modelValue?.name) {
            const sectionIndex = sections.value.findIndex((s: FlowSection) => s.name === props.modelValue?.name);

            if (sectionIndex !== -1 && sections.value[sectionIndex]) {
                const section: FlowSection = sections.value[sectionIndex];

                if (section) {
                    section.name = name;
                    section.stop_at = stopAt;
                }
            }

            await saveSections(sections.value);
        } else {
            await addSection(name, stopAt);
        }

        visible.value = false;
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};

watch(visible, (newVisible) => {
    if (!newVisible) {
        validation.value.$reset();
        scheduleStopAt.value = false;
    } else {
        section.value = {
            name: props.modelValue?.name || defaultName.value,
            stopAt: props.modelValue?.stopAt ? dayjs(props.modelValue.stopAt).tz(getClientTz()).toDate() : undefined,
        };

        scheduleStopAt.value = Boolean(props.modelValue?.stopAt);
    }
});
</script>
